import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "../../../Menu/Settings/api/axiosBaseQuery";
import { SetDataTrip } from "../../../../Actions/Trip";
import GetCookie from "../../../Common/Functions/GetCookie";
import { TripVersion } from "../../../Menu/MaterialTripList/objects/tripVersion";
import {
    CancelResult,
    ExtendEligibility,
    ExtendResult,
    ForceValidateResult,
    OptionEligibility
} from "../objects/lockableFare";

// Version-scoped endpoints are nested under `trip/{trip_id}/versions/{trip_version_id}/...`.
// The trip id comes from the `trip_id` cookie (same source as onRefreshPrice / refreshTripVersion),
// so callers only pass the version id.
const versionUrl = (versionId: number, action: string): string =>
    `/trip/${GetCookie('trip_id')}/versions/${versionId}/${action}/`;

export const lockableFareApi = createApi({
    reducerPath: 'api.lockableFare',
    baseQuery: axiosBaseQuery({
        baseUrl: () => `${API_HREF}client/${window.id_owner}`
    }),
    // Eligibility depends on the cart's products. Refetch on every (re)mount so re-entering the
    // cart after modifying products always re-evaluates eligibility (leaving/returning to the
    // cart-recap unmounts and remounts its consumers). The optionEligibilityInvalidationMiddleware
    // still invalidates this tag on product add/delete for the same-mount case; the two are
    // complementary.
    refetchOnMountOrArgChange: true,
    tagTypes: ['option-eligibility'],
    endpoints: (builder) => ({
        getOptionEligibility: builder.query<OptionEligibility, { versionId: number }>({
            query: ({ versionId }) => ({
                url: versionUrl(versionId, 'option_eligibility'),
                method: 'GET',
                params: { token: GetCookie('trip_token') }
            }),
            providesTags: ['option-eligibility']
        }),
        getExtendOptionEligibility: builder.query<ExtendEligibility, { versionId: number }>({
            query: ({ versionId }) => ({
                url: versionUrl(versionId, 'extend_option_eligibility'),
                method: 'GET',
                params: { token: GetCookie('trip_token') }
            }),
            providesTags: ['option-eligibility']
        }),
        setOption: builder.mutation<TripVersion, { versionId: number }>({
            query: ({ versionId }) => ({
                url: versionUrl(versionId, 'set_option'),
                method: 'POST',
                data: {},
                params: { token: GetCookie('trip_token') }
            }),
            // set_option returns the full updated version → refresh the trip slice so the
            // header pill, cart and product tables update immediately.
            async onQueryStarted(arg, { dispatch, queryFulfilled }) {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(SetDataTrip(data));
                } catch {
                    // error is surfaced to the caller through the mutation result
                }
            }
        }),
        extendOption: builder.mutation<ExtendResult, { versionId: number }>({
            query: ({ versionId }) => ({
                url: versionUrl(versionId, 'extend_option'),
                method: 'POST',
                data: {},
                params: { token: GetCookie('trip_token') }
            }),
            // Extending consumes the single allowed extension, so the backend now reports
            // can_extend: false. Invalidate so getExtendOptionEligibility refetches and the
            // "Give the customer more time" action disappears without a page reload.
            invalidatesTags: ['option-eligibility']
        }),
        // cancel_option / force_validate_option are logged-in-only and take NO token.
        cancelOption: builder.mutation<CancelResult, { versionId: number }>({
            query: ({ versionId }) => ({
                url: versionUrl(versionId, 'cancel_option'),
                method: 'POST',
                data: {}
            }),
            invalidatesTags: ['option-eligibility']
        }),
        // force_validate is a bulk endpoint: not version-scoped in the path, ids in the body.
        forceValidateOption: builder.mutation<ForceValidateResult, { versionId: number }>({
            query: ({ versionId }) => ({
                url: `/trip/bulk_force_validate_option/`,
                method: 'POST',
                data: { ids: [versionId] }
            }),
            invalidatesTags: ['option-eligibility']
        })
    })
});

export const {
    useGetOptionEligibilityQuery,
    useLazyGetOptionEligibilityQuery,
    useGetExtendOptionEligibilityQuery,
    useLazyGetExtendOptionEligibilityQuery,
    useSetOptionMutation,
    useExtendOptionMutation,
    useCancelOptionMutation,
    useForceValidateOptionMutation
} = lockableFareApi;
